import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";
// eslint-disable-next-line
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { IAuthContext, useAuth } from "../contexts/AuthContext";
import { Navbar } from "@/components/layout/Navbar";

export const Route = createRootRouteWithContext<{ auth: IAuthContext }>()({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: RootComponent, // eslint-disable-line  @typescript-eslint/no-use-before-define
});

function RootComponent() {
  const { isLoggedIn, loading } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const location = useLocation();
  const routerState = useRouterState();
  const search = Route.useSearch();

  React.useEffect(() => {
    if (loading) return;

    const shouldRedirectToHome = isLoggedIn && location.pathname === "/login";
    const shouldRedirectToLogin =
      !isLoggedIn &&
      location.pathname !== "/" &&
      location.pathname !== "/login" &&
      location.pathname !== "/login-link-modal" &&
      location.pathname !== "/login-link";

    if (shouldRedirectToHome) {
      const to = search.redirect || "/library";
      navigate({ to, replace: true });
    }
    if (shouldRedirectToLogin) {
      navigate({
        to: "/login",
        replace: true,
        search: {
          redirect: routerState.location.href,
        },
      });
    }
  }, [
    isLoggedIn,
    loading,
    location.pathname,
    navigate,
    routerState.location.href,
    routerState.location.pathname,
    search.redirect,
  ]);

  const isSplashRoute = location.pathname === "/";
  const isFullWidthRoute = location.pathname === "/library" || location.pathname === "/catalog";
  const isImageRoute = location.pathname.endsWith("/image");
  const isBookRoute = location.pathname.startsWith("/book/");
  const isReadContentRoute = location.pathname.includes("/read/") && location.pathname.endsWith("/content");
  const isReadVisualsRoute = location.pathname.includes("/read/") && location.pathname.endsWith("/visuals");
  const isReadCombinedRoute = location.pathname.includes("/read/") && location.pathname.endsWith("/combined");
  const isReadFullscreenRoute = isReadContentRoute || isReadVisualsRoute || isReadCombinedRoute;

  if (isSplashRoute) {
    return <Outlet />;
  }

  // Image view / read visuals: no chrome at all
  if (isImageRoute || isReadVisualsRoute) {
    return (
      <div className="bg-secondary-900 min-h-screen">
        <Outlet />
      </div>
    );
  }

  // Read content / combined window: minimal chrome
  if (isReadContentRoute || isReadCombinedRoute) {
    return (
      <div className="bg-warm-cream min-h-screen">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="bg-warm-cream min-h-screen">
      {!isBookRoute && !isReadFullscreenRoute && <Navbar />}
      <div
        className={
          isBookRoute
            ? ""
            : isFullWidthRoute
              ? "max-w-[100vw] mx-auto px-[1.5rem]"
              : "max-w-[100rem] mx-auto px-[1.5rem]"
        }
      >
        <Outlet />
      </div>
      {import.meta.env.VITE_ENVIRONMENT === "local" && (
        <TanStackRouterDevtools />
      )}
    </div>
  );
}
